import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Info, Loader2, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createMatch, fetchDailyChallenge } from '~/api/matches';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import type { SessionUser } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { OnboardingCoach } from '~/ui/OnboardingCoach';
import { Button } from '~/ui/components/button';
import { Card, CardDescription, CardHeader, CardTitle } from '~/ui/components/card';

export const Route = createFileRoute('/play')({
  head: () =>
    buildHead({
      title: 'Play',
      description:
        'Choose your battle: quickplay, ranked by genre, Sample Flip remixes, or the Daily Challenge. 8-player free-for-all or private rooms 1v1 to 5v5.',
      path: '/play',
    }),
  component: PlayMenu,
});

type Genre = { id: string; slug: string; name: string; kind: 'system' | 'user' };

function PlayMenu() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);

  useEffect(() => {
    api<{ items: Genre[] }>('/genres').then((r) => setGenres(r.items));
  }, []);

  const createMatchMut = useMutation({
    mutationFn: createMatch,
    onSuccess: (match) => {
      navigate({ to: '/room/$code', params: { code: match.roomCode } });
    },
    onSettled: () => setActiveMode(null),
  });

  const startQuickPlay = () => {
    setActiveMode('quickplay');
    createMatchMut.mutate({ mode: 'quickplay' });
  };

  const startRanked = (genreSlug: string) => {
    setActiveMode('ranked');
    // Ranked never honours a client-picked pack - the server picks at random
    // so nobody can stack their stems. Pack picking is private-room only.
    createMatchMut.mutate({ mode: 'ranked', genreSlug });
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-12 space-y-6">
      <h2 className="text-3xl font-bold">How do you want to play?</h2>

      <OnboardingCoach />

      {session && <ActiveMatchesCallout />}

      {createMatchMut.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm">
          Couldn't start a match: {String(createMatchMut.error)}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Quick Play */}
        <button
          type="button"
          disabled={createMatchMut.isPending}
          onClick={startQuickPlay}
          title="Free-for-all up to 8 producers. Random system genre + sample pack. 10-minute submit window. Blind audience voting at the end. No rating impact - this is the warm-up mode. Auto-starts 90s after the third producer joins."
          className="text-left focus:outline-none focus:ring-2 focus:ring-ring rounded-lg disabled:opacity-60"
        >
          <Card className="h-full transition hover:border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Quick Play
                <ModeInfo text="Free-for-all up to 8 producers. The server picks a random system genre + sample pack. 10-minute submit window. Blind audience voting at the end. No rating impact - this is the warm-up mode. Auto-starts 90s after the third producer joins, or instantly if the room fills." />
                {activeMode === 'quickplay' && createMatchMut.isPending && (
                  <Loader2 className="size-4 animate-spin" />
                )}
              </CardTitle>
              <CardDescription>
                Up to 8 producers. Random genre, 10-min flip.{' '}
                {session ? 'Free, unlimited.' : 'Free, 3/day for guests; sign in for unlimited.'}
              </CardDescription>
            </CardHeader>
          </Card>
        </button>

        {/* Ranked */}
        <RankedCard
          genres={genres}
          session={session}
          onStart={startRanked}
          busy={createMatchMut.isPending && activeMode === 'ranked'}
        />

        {/* Private Room */}
        <PrivateRoomCard
          genres={genres}
          session={session}
          busy={createMatchMut.isPending && activeMode === 'private'}
          onStart={(body) => {
            setActiveMode('private');
            createMatchMut.mutate({ mode: 'private', ...body });
          }}
          // PrivateRoomCard already includes samplePackId in body
        />

        {/* Join by Code */}
        <JoinByCodeCard />

        {/* Daily Challenge - shared async board, rotates at 00:00 UTC */}
        <DailyChallengeCard session={session} />

        {/* Sample Flip - one loop, everyone flips it */}
        <SampleFlipCard
          genres={genres}
          busy={createMatchMut.isPending && activeMode === 'flip'}
          onStart={(genreSlug) => {
            setActiveMode('flip');
            createMatchMut.mutate({ mode: 'flip', genreSlug });
          }}
        />
      </div>
    </section>
  );
}

function DailyChallengeCard({ session }: { session: SessionUser | null }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [today, setToday] = useState<{
    date: string;
    genre: { slug: string; name: string };
    samplePack: { name: string } | null;
    roomCode: string;
    submissionCount: number;
    cap: number;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Determine if the user has access: must be paid or admin.
  const hasAccess = session !== null && (session.plan === 'paid' || session.role === 'admin');

  useEffect(() => {
    // Only fetch daily challenge data when the user has access; skip for
    // locked users so they never receive a 402 response in the console.
    if (!hasAccess) return;
    fetchDailyChallenge()
      .then(setToday)
      .catch((e) => setErr((e as Error).message));
  }, [hasAccess]);

  const enter = async () => {
    if (!today) return;
    setBusy(true);
    try {
      // Re-fetch to get the latest room code (handles midnight rollover).
      const fresh = await fetchDailyChallenge();
      navigate({ to: '/room/$code', params: { code: fresh.roomCode } });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Locked tile: shown for anonymous visitors and free-tier users. The copy
  // splits these two cases so the next-step is obvious - anon users sign in,
  // signed-in free users wait for the (still-being-built) upgrade flow.
  if (!hasAccess) {
    const isAnon = session === null;
    return (
      <Card className="h-full border-accent/40 bg-accent/5 opacity-80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Daily Challenge
            <ModeInfo text="One shared challenge per day, same genre + sample pack for everyone, async (you don't need anyone else online). Day 1 you submit, day 2 voting opens for any signed-in user, day 3 results lock. Supporter perk." />
            <Lock className="size-4 text-muted-foreground" />
            <span className="text-sm font-normal text-muted-foreground">Supporter perk</span>
          </CardTitle>
          <CardDescription>
            Today&apos;s shared beat challenge. Rotates at 00:00 UTC.
          </CardDescription>
          <p className="mt-2 text-xs text-muted-foreground">
            {isAnon ? (
              <>
                <a href="/auth/sign-in" className="underline hover:text-foreground">
                  Sign in
                </a>{' '}
                first. Daily Challenge is a Supporter perk (€2,95/mo).
              </>
            ) : (
              <>
                Daily Challenge is a Supporter perk (€2,95/mo).{' '}
                <a href="/pricing" className="underline hover:text-foreground">
                  Become a Supporter
                </a>
              </>
            )}
          </p>
        </CardHeader>
      </Card>
    );
  }

  return (
    <button
      type="button"
      disabled={busy || !today}
      onClick={enter}
      className="text-left focus:outline-none focus:ring-2 focus:ring-ring rounded-lg disabled:opacity-60"
    >
      <Card className="h-full border-accent/40 bg-accent/5 transition hover:border-accent">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Daily Challenge
            <ModeInfo text="One shared challenge per day, same genre + sample pack for everyone, async (you don't need anyone else online). Day 1 you submit, day 2 voting opens for any signed-in user, day 3 results lock." />
            {busy && <Loader2 className="size-4 animate-spin" />}
          </CardTitle>
          {err ? (
            <CardDescription className="text-destructive">{err}</CardDescription>
          ) : today ? (
            <CardDescription>
              Today: <span className="font-semibold text-foreground">{today.genre.name}</span>
              {today.samplePack ? ` - pack: ${today.samplePack.name}` : ''}.{' '}
              <span className="font-medium text-foreground">
                {today.submissionCount}/{today.cap}
              </span>{' '}
              submissions today. Resets at 00:00 UTC.
            </CardDescription>
          ) : (
            <CardDescription>Loading today&apos;s prompt...</CardDescription>
          )}
        </CardHeader>
      </Card>
    </button>
  );
}

function SampleFlipCard({
  genres,
  busy,
  onStart,
}: {
  genres: Genre[];
  busy: boolean;
  onStart: (genreSlug: string | undefined) => void;
}) {
  const [slug, setSlug] = useState('');
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    api<{ items: unknown[] }>('/flip-sources')
      .then((r) => setTotal(r.items.length))
      .catch(() => setTotal(0));
  }, []);

  const disabled = busy || total === 0;

  return (
    <Card className="h-full border-primary/40 bg-primary/5 transition hover:border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Sample Flip
          <ModeInfo text="One audio loop (a vocal chop, a melody hook), everyone in the room flips it their way. No drum kit, no extra samples - the whole match is built from that one source. Tests creative reuse rather than arrangement around a kit. 10-min flip, blind vote, free." />
          {busy && <Loader2 className="size-4 animate-spin" />}
        </CardTitle>
        <CardDescription>
          {total === null
            ? 'Loading loops...'
            : total === 0
              ? 'No flip loops available yet. Ask an admin to seed some.'
              : `${total} loops ready. Everyone in the room flips the same source.`}
        </CardDescription>
        <div className="mt-3 flex flex-col gap-2">
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">random genre</option>
            {genres
              .filter((g) => g.kind === 'system')
              .map((g) => (
                <option key={g.id} value={g.slug}>
                  {g.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onStart(slug || undefined)}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? 'Starting...' : 'Flip it'}
          </button>
        </div>
      </CardHeader>
    </Card>
  );
}

// ─── Pack picker hook ───────────────────────────────────────────────────────
// Fetches packs the caller can pick (pool packs + their own uploaded ones)
// for a chosen genre. Re-fetches whenever the genre changes. Returns the
// list and a setter for the chosen samplePackId.

type SelectablePack = {
  id: string;
  name: string;
  kind: 'pool' | 'uploaded';
  genre: { slug: string; name: string };
  stemCount: number;
  isMine: boolean;
};

function usePackPicker(genreSlug: string) {
  const [packs, setPacks] = useState<SelectablePack[]>([]);
  const [packId, setPackId] = useState<string>('');
  // Re-fetch on genre change. Empty genre = no fetch (some cards do
  // server-pick).
  useEffect(() => {
    setPackId('');
    if (!genreSlug) {
      setPacks([]);
      return;
    }
    api<{ items: SelectablePack[] }>(`/sample-packs?genreSlug=${encodeURIComponent(genreSlug)}`)
      .then((r) => setPacks(r.items))
      .catch(() => setPacks([]));
  }, [genreSlug]);
  return { packs, packId, setPackId };
}

function PackPickerSelect({
  packs,
  packId,
  setPackId,
  genreSelected,
}: {
  packs: SelectablePack[];
  packId: string;
  setPackId: (s: string) => void;
  genreSelected: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      <span>Sample pack</span>
      <select
        value={packId}
        onChange={(e) => setPackId(e.target.value)}
        disabled={!genreSelected}
        className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50"
        title={genreSelected ? '' : 'Pick a genre first'}
      >
        <option value="">
          {!genreSelected
            ? 'pick a genre first'
            : packs.length === 0
              ? 'no packs for this genre'
              : 'random pool pack'}
        </option>
        {packs.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({p.stemCount} stems
            {p.kind === 'uploaded' ? (p.isMine ? ' · yours' : ' · uploaded') : ''})
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Ranked card ────────────────────────────────────────────────────────────

function RankedCard({
  genres,
  session,
  onStart,
  busy,
}: {
  genres: Genre[];
  session: { handle?: string | null } | null;
  onStart: (genreSlug: string) => void;
  busy: boolean;
}) {
  const [slug, setSlug] = useState<string>('');

  if (!session) {
    return (
      <a
        href="/auth/sign-in"
        className="focus:outline-none focus:ring-2 focus:ring-ring rounded-lg"
      >
        <Card className="h-full opacity-70 transition hover:border-primary hover:opacity-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Ranked
              <ModeInfo text="8-player FFA per genre with a Glicko-2-derived rating + LP system. Pick a genre, win to climb tiers (Bronze through Grandmaster). First 10 ranked matches are calibration with no LP shown. Honor 50+ required. Sign in to play." />
            </CardTitle>
            <CardDescription>Climb the leaderboard. Sign in to play.</CardDescription>
          </CardHeader>
        </Card>
      </a>
    );
  }
  return (
    <Card className="h-full transition hover:border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Ranked
          <ModeInfo text="8-player FFA per genre with a Glicko-2-derived rating + LP system. Pick a genre, win to climb tiers (Bronze through Grandmaster). First 10 ranked matches are calibration with no LP shown. Honor 50+ required." />
          {busy && <Loader2 className="size-4 animate-spin" />}
        </CardTitle>
        <CardDescription>Pick a genre. Every win is +10 on the leaderboard.</CardDescription>
        <div className="mt-3 flex flex-col gap-2">
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">- pick a genre -</option>
            {genres
              .filter((g) => g.kind === 'system')
              .map((g) => (
                <option key={g.id} value={g.slug}>
                  {g.name}
                </option>
              ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Sample pack is randomised by the server - everyone gets the same fair shot.
          </p>
          <button
            type="button"
            disabled={!slug || busy}
            onClick={() => onStart(slug)}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? 'Starting...' : 'Play Ranked'}
          </button>
        </div>
      </CardHeader>
    </Card>
  );
}

// ─── Private room card ──────────────────────────────────────────────────────

function PrivateRoomCard({
  genres,
  session,
  onStart,
  busy,
}: {
  genres: Genre[];
  session: { handle?: string | null } | null;
  onStart: (body: {
    genreSlug: string;
    teamSize: number;
    teamCount: number;
    submitSeconds: number;
    samplePackId?: string;
    isPublic?: boolean;
  }) => void;
  busy: boolean;
}) {
  const [slug, setSlug] = useState('');
  const [mins, setMins] = useState(5);
  const [isPublic, setIsPublic] = useState(false);
  const { packs, packId, setPackId } = usePackPicker(slug);

  if (!session) {
    return (
      <a
        href="/auth/sign-in"
        className="focus:outline-none focus:ring-2 focus:ring-ring rounded-lg"
      >
        <Card className="h-full opacity-70 transition hover:border-primary hover:opacity-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Private Room
              <ModeInfo text="Host a closed match for 2 to 8 producers (host starts when everyone is ready) with a room code you share. Pick the genre + sample pack + submit window length (1-60 min). No rating impact. Use it for crew battles, classroom sessions, or testing a beat against trusted ears. Sign in to host." />
            </CardTitle>
            <CardDescription>Invite-only lobbies. Sign in to host.</CardDescription>
          </CardHeader>
        </Card>
      </a>
    );
  }

  // FFA-only for now. Team formats (1v1, 2v2, 3v3) will come back once
  // coordinated team submissions and scoring are ready.
  // Capacity is fixed at 8 - the room can hold up to 8 producers but the
  // host starts whenever everyone present is ready (min 2 seated). No
  // pre-pinned bracket size; total players doesn't matter.
  const preset = { teamSize: 1, teamCount: 8 };

  return (
    <Card className="h-full transition hover:border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Private Room
          <ModeInfo text="Host a closed match for 2 to 8 producers (host starts when everyone is ready) with a room code you share. Pick the genre + sample pack + submit window length (1-60 min). No rating impact. Use it for crew battles, classroom sessions, or testing a beat against trusted ears." />
          {busy && <Loader2 className="size-4 animate-spin" />}
        </CardTitle>
        <CardDescription>Invite friends with the 6-char room code.</CardDescription>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="col-span-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">- pick a genre -</option>
            {genres.map((g) => (
              <option key={g.id} value={g.slug}>
                {g.name} ({g.kind})
              </option>
            ))}
          </select>
          <select
            value={mins}
            onChange={(e) => setMins(Number(e.target.value))}
            className="col-span-2 rounded-md border border-border bg-background px-2 py-2 text-sm"
          >
            {[5, 10, 20, 30, 50, 60].map((m) => (
              <option key={m} value={m}>
                {m}-min submit window
              </option>
            ))}
          </select>
          <div className="col-span-2">
            <PackPickerSelect
              packs={packs}
              packId={packId}
              setPackId={setPackId}
              genreSelected={Boolean(slug)}
            />
          </div>
          <label className="col-span-2 flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="mt-0.5 size-3.5 cursor-pointer"
            />
            <span>
              List publicly in <span className="font-mono">/lobbies</span> so anyone can join.
              Default off; share the room code with friends instead.
            </span>
          </label>
          <button
            type="button"
            disabled={!slug || busy}
            onClick={() =>
              onStart({
                genreSlug: slug,
                teamSize: preset.teamSize,
                teamCount: preset.teamCount,
                submitSeconds: mins * 60,
                samplePackId: packId || undefined,
                isPublic,
              })
            }
            className="col-span-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? 'Creating room...' : 'Host a room'}
          </button>
        </div>
      </CardHeader>
    </Card>
  );
}

// ─── Join by code card ──────────────────────────────────────────────────────

function JoinByCodeCard() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    setErr(null);
    const trimmed = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,10}$/.test(trimmed)) {
      setErr('Codes are 4-10 letters / digits.');
      return;
    }
    setBusy(true);
    try {
      // Validate first so we can give a clean "no such room" instead of
      // routing to a broken page.
      await api(`/matches/${trimmed}`);
      navigate({ to: '/room/$code', params: { code: trimmed } });
    } catch (e) {
      setErr(
        /404/.test(String(e)) ? 'No room with that code.' : (e as Error).message.slice(0, 120),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="h-full transition hover:border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Join by Code
          <ModeInfo text="Got a 6-character code from someone hosting a private room or tournament match? Paste it here to drop straight into their lobby." />
        </CardTitle>
        <CardDescription>Got a 6-character room code? Drop in.</CardDescription>
        <div className="mt-3 flex flex-col gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={10}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm font-mono uppercase tracking-widest"
            onKeyDown={(e) => {
              if (e.key === 'Enter') go();
            }}
          />
          {err && <span className="text-xs text-red-500">{err}</span>}
          <button
            type="button"
            onClick={go}
            disabled={busy || !code}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? 'Joining...' : 'Join'}
          </button>
        </div>
      </CardHeader>
    </Card>
  );
}

type ActiveMatch = {
  roomCode: string;
  mode: string;
  status: string;
  currentPhase: string | null;
  genreSlug: string;
  genreName: string;
  joinedAt: string;
};

function ActiveMatchesCallout() {
  const { data } = useQuery({
    queryKey: ['me', 'active-matches'],
    queryFn: () => api<{ items: ActiveMatch[] }>('/me/active-matches'),
    staleTime: 30_000,
  });
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3">
      <div className="text-sm font-semibold text-emerald-300">
        You're already in {items.length === 1 ? 'a match' : `${items.length} matches`}
      </div>
      <ul className="space-y-2">
        {items.map((m) => (
          <li
            key={m.roomCode}
            className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 px-3 py-2"
          >
            <div className="text-sm">
              <span className="font-mono text-primary">{m.roomCode}</span>
              <span className="text-muted-foreground">
                {' '}
                · {m.mode} · {m.genreName} ·{' '}
                <span className="capitalize">{m.currentPhase ?? m.status}</span>
              </span>
            </div>
            <Button asChild size="sm">
              <Link to="/room/$code" params={{ code: m.roomCode }}>
                Rejoin <ArrowRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModeInfo({ text }: { text: string }) {
  // Native title attribute renders as the system tooltip on hover and is read
  // out by screen readers. Wrapping in <span tabIndex=0> makes it focusable
  // by keyboard so the tooltip is reachable without a mouse.
  return (
    <span
      role="tooltip"
      aria-label="More info"
      title={text}
      className="inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:text-foreground"
    >
      <Info className="size-3.5" />
    </span>
  );
}
